/**
 * <menu-drawer> Web Component
 *
 * A toggle-able drawer for mobile navigation.
 * Uses the 'open' attribute to track state and supports
 * closing via Escape key or clicking an overlay.
 *
 * Supports two modes:
 * 1. Self-contained: toggle, panel, close, overlay are all children
 * 2. Disconnected: toggle is outside (uses data-drawer-target on the toggle
 *    and data-drawer-id on the menu-drawer to link them)
 *
 * Usage (self-contained):
 *   <menu-drawer>
 *     <button class="menu-drawer__toggle">☰</button>
 *     <div class="menu-drawer__panel">...</div>
 *     <div class="menu-drawer__overlay"></div>
 *   </menu-drawer>
 *
 * Usage (disconnected):
 *   <button data-drawer-target="my-drawer">☰</button>
 *   ...
 *   <menu-drawer data-drawer-id="my-drawer">
 *     <div class="menu-drawer__panel">...</div>
 *     <div class="menu-drawer__overlay"></div>
 *   </menu-drawer>
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.externalToggle = null;
    this.toggle = null;
    this.panel = null;
    this.closeBtn = null;
    this.overlay = null;

    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.toggle = this.querySelector('.menu-drawer__toggle');
    this.panel = this.querySelector('.menu-drawer__panel');
    this.closeBtn = this.querySelector('.menu-drawer__close');
    this.overlay = this.querySelector('.menu-drawer__overlay');

    // Self-contained toggle
    this.toggle?.addEventListener('click', () => this.open());

    // Disconnected toggle: find external button by data-drawer-target
    const drawerId = this.dataset.drawerId;
    if (drawerId) {
      this.externalToggle = document.querySelector(
        `[data-drawer-target="${drawerId}"]`,
      );
      this.externalToggle?.addEventListener('click', () => this.open());
    }

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
  }

  open() {
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    if (this.externalToggle) {
      this.externalToggle.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeyDown);

    this.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  close() {
    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    if (this.externalToggle) {
      this.externalToggle.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeyDown);

    // Close any active header-dropdown subdrawers inside this drawer
    this.querySelectorAll('header-dropdown[open]').forEach((dropdown) => {
      dropdown.close();
    });

    this.dispatchEvent(new CustomEvent('menu-drawer:close', { bubbles: true }));
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Observed attributes for external state control.
   */
  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      const isOpen = newValue !== null;
      this.panel?.classList.toggle('is-active', isOpen);
      this.overlay?.classList.toggle('is-active', isOpen);
    }
  }
}

customElements.define('menu-drawer', MenuDrawer);
